import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Badge,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowBack as BackIcon,
  ArrowUpward as ArrowUpwardIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  EditOutlined as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Lock as LockIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Badge as BadgeIcon,
  AccountBalanceWallet as WalletIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { AccountMovement, Customer, Debt, Role } from '../../types';
import { apiClient } from '../../services/api';
import { accountMovementDescription, toEditableDebt } from '../../utils/accountMovement';
import { customerAccountAmountColor, formatCustomerAccountAmount } from '../../utils/customerAccountAmount';
import { errorMessage } from '../../utils/errorMessage';
import { formatDate, formatDateTime } from '../../utils/date';
import { returnFromCustomerDetail } from '../../utils/customerNavigation';
import { nextTableSort } from '../../utils/tableSort';
import RowActionsButton from '../common/RowActionsButton';
import { createCustomerDebtPdf } from '../../utils/customerDebtPdf';
import { createCustomerDebtSummary, createCustomerDebtSummaryDocument, CustomerDebtSummaryInput, CustomerDebtSummaryLabels } from '../../utils/customerDebtSummary';
import { DebtDialog, DeleteDebtDialog, PaymentDialog } from '../debt';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import { DebtMovementIcon, isSaleLinked, movementKind } from '../debt/DebtMovementIcon';
import { useIsMobileShell } from '../common/useIsMobileShell';
import { openWhatsApp } from '../../utils/whatsapp';
import CustomerPaymentPlans from './CustomerPaymentPlans';

type LedgerFilter = 'ALL' | 'DEBT' | 'PAYMENT';
type SharePeriod = 'LAST_MONTH' | 'LAST_TWO_MONTHS' | 'LAST_YEAR' | 'ALL' | 'CUSTOM';
type SortField = 'lastActivityDateTime' | 'description' | 'createdByUsername' | 'amount';

const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobileShell();
  const queryClient = useQueryClient();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [debts, setDebts] = useState<AccountMovement[]>([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ledgerFilter, setLedgerFilter] = useState<LedgerFilter>('ALL');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [movementSort, setMovementSort] = useState<{ sort?: string; direction?: 'asc' | 'desc' }>({ sort: 'lastActivityDateTime', direction: 'desc' });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuDebt, setMenuDebt] = useState<Debt | null>(null);
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [saleMovementNoticeOpen, setSaleMovementNoticeOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sharePeriod, setSharePeriod] = useState<SharePeriod>('LAST_MONTH');
  const [shareStartDate, setShareStartDate] = useState('');
  const [shareEndDate, setShareEndDate] = useState('');
  const [shareMessageEdited, setShareMessageEdited] = useState(false);

  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState<Debt | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState<Debt | null>(null);

  const canManageDebts = user?.role === Role.COMPANY_ADMIN || user?.role === Role.EMPLOYEE;
  const customerNameFromState = location.state?.customerName as string | undefined;
  const customerName = customerNameFromState || `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`.trim();

  const summaryLabels = useMemo<CustomerDebtSummaryLabels>(() => ({
    summaryTitle: t('customerDetail.summaryTitle'),
    customerLabel: t('customerDetail.summaryCustomer'),
    balanceLabel: t('customerDetail.summaryBalance'),
    dateLabel: t('customerDetail.summaryDate'),
    recentMovementsLabel: t('customerDetail.summaryRecentMovements'),
    noRecentMovements: t('customerDetail.summaryNoMovements'),
    debtLabel: t('customerDetail.summaryDebt'),
    paymentLabel: t('customerDetail.summaryPayment'),
    customerOwes: t('customerDetail.customerOwes'),
    youOwe: t('customerDetail.youOwe'),
    noBalance: t('customerDetail.noBalance'),
    noDescription: t('customerDetail.summaryNoDescription'),
  }), [t]);
  const summaryGeneratedAt = useMemo(() => formatDate(new Date(), language), [language]);
  const summaryMovements = useMemo(() => [...debts]
    .sort((a, b) => (b.lastActivityDateTime || b.transactionDateTime || b.transactionDate).localeCompare(a.lastActivityDateTime || a.transactionDateTime || a.transactionDate))
    .map(debt => ({
      description: accountMovementDescription(debt, t),
      date: formatDateTime(debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' }),
      sortDate: (debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate).slice(0, 10),
      amount: debt.amount,
    })), [debts, language, t]);
  const movementDate = useCallback((movement: AccountMovement) => (
    movement.lastActivityDateTime || movement.transactionDateTime || movement.transactionDate
  ), []);
  const sharePeriodLabel = useMemo(() => {
    if (sharePeriod === 'LAST_MONTH') return t('customerDetail.lastMonth');
    if (sharePeriod === 'LAST_TWO_MONTHS') return t('customerDetail.lastTwoMonths');
    if (sharePeriod === 'LAST_YEAR') return t('customerDetail.lastYear');
    if (sharePeriod === 'ALL') return t('customerDetail.allMovements');
    return shareStartDate && shareEndDate ? `${shareStartDate} – ${shareEndDate}` : t('customerDetail.customRange');
  }, [shareEndDate, sharePeriod, shareStartDate, t]);
  const shareDateRange = useMemo(() => {
    if (sharePeriod === 'ALL') return { start: '', end: '' };
    if (sharePeriod === 'CUSTOM') return { start: shareStartDate, end: shareEndDate };
    const end = new Date();
    const start = new Date(end);
    if (sharePeriod === 'LAST_YEAR') start.setFullYear(start.getFullYear() - 1);
    else if (sharePeriod === 'LAST_TWO_MONTHS') start.setMonth(start.getMonth() - 2);
    else start.setMonth(start.getMonth() - 1);
    const toDateInput = (value: Date) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    return { start: toDateInput(start), end: toDateInput(end) };
  }, [shareEndDate, sharePeriod, shareStartDate]);
  const shareSummaryMovements = useMemo(() => {
    if (shareDateRange.start && shareDateRange.end && shareDateRange.start > shareDateRange.end) return [];
    return summaryMovements.filter(movement => {
      if (!shareDateRange.start && !shareDateRange.end) return true;
      return (!shareDateRange.start || (movement.sortDate || '') >= shareDateRange.start)
        && (!shareDateRange.end || (movement.sortDate || '') <= shareDateRange.end);
    });
  }, [shareDateRange, summaryMovements]);
  const summaryInput = useMemo<CustomerDebtSummaryInput>(() => ({
    customerName,
    totalDebt,
    generatedAt: summaryGeneratedAt,
    period: sharePeriodLabel,
    periodLabel: t('customerDetail.summaryPeriod'),
    recentMovements: shareSummaryMovements,
    labels: summaryLabels,
  }), [customerName, sharePeriodLabel, shareSummaryMovements, summaryGeneratedAt, summaryLabels, t, totalDebt]);
  const debtSummary = useMemo(() => createCustomerDebtSummary(summaryInput), [summaryInput]);

  useEffect(() => {
    if (shareDialogOpen && !shareMessageEdited) setShareMessage(debtSummary.message);
  }, [debtSummary.message, shareDialogOpen, shareMessageEdited]);

  useEffect(() => {
    if (!customer || !isMobile) return;
    const loadedName = `${customer.firstName} ${customer.lastName}`.trim();
    if (customerNameFromState !== loadedName) {
      navigate(location.pathname, { replace: true, state: { ...location.state, customerName: loadedName } });
    }
  }, [customer, customerNameFromState, isMobile, location.pathname, location.state, navigate]);

  const loadDebts = useCallback(async () => {
    if (!customerId) return;
    try {
      const debtsData = await apiClient.getAccountMovementsByCustomer(customerId);
      setDebts(debtsData);
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, t('debts.loadError')));
    }
  }, [customerId, t]);

  useEffect(() => {
    void loadDebts();
  }, [loadDebts]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!customerId) return;
      try {
        setLoading(true);
        setError(null);
        const [customerData, totalDebtData] = await Promise.all([
          apiClient.getCustomer(customerId),
          apiClient.getCustomerTotalDebt(customerId),
        ]);
        setCustomer(customerData);
        setTotalDebt(totalDebtData.totalDebt);
      } catch (loadError: unknown) {
        setError(errorMessage(loadError, t('customers.loadError')));
      } finally {
        setLoading(false);
      }
    };
    void loadInitialData();
  }, [customerId, t]);

  const handleBack = () => returnFromCustomerDetail(navigate, location.state);
  const handleAddDebt = () => {
    setSelectedDebt(undefined);
    setDebtDialogOpen(true);
  };
  const handleAddPayment = () => setPaymentDialogOpen(true);
  const handleEditDebt = (debt: Debt) => {
    setSelectedDebt(debt);
    setDebtDialogOpen(true);
  };
  const handleDeleteDebt = (debt: Debt) => {
    setDebtToDelete(debt);
    setDeleteDialogOpen(true);
  };
  const closeMovementMenu = () => {
    setMenuAnchor(null);
    setMenuDebt(null);
  };

  const yearOptions = useMemo(() => {
    const years = debts.map(debt => movementDate(debt).slice(0, 4))
      .filter(year => /^\d{4}$/.test(year)).map(Number);
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [debts, movementDate]);
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, index) => index + 1), []);
  const filteredDebts = useMemo(() => debts.filter(debt => {
    const dateValue = movementDate(debt);
    const year = Number(dateValue.slice(0, 4));
    const month = Number(dateValue.slice(5, 7));
      const kind = movementKind(debt);
      const matchesType = ledgerFilter === 'ALL'
      || (ledgerFilter === 'DEBT' && kind !== 'payment')
      || (ledgerFilter === 'PAYMENT' && kind === 'payment');
    return matchesType && (selectedYear === '' || year === selectedYear)
      && (selectedMonth === '' || month === selectedMonth);
  }), [debts, ledgerFilter, movementDate, selectedMonth, selectedYear]);
  const sortedDebts = useMemo(() => [...filteredDebts].sort((a, b) => {
    const sortField = movementSort.sort || 'lastActivityDateTime';
    let comparison = 0;
    if (sortField === 'amount') comparison = a.amount - b.amount;
    else if (sortField === 'description') comparison = accountMovementDescription(a, t).localeCompare(accountMovementDescription(b, t), language);
    else if (sortField === 'createdByUsername') comparison = (a.createdByUsername || '').localeCompare(b.createdByUsername || '', language);
    else comparison = movementDate(a).localeCompare(movementDate(b));
    return (movementSort.direction || 'desc') === 'desc' ? -comparison : comparison;
  }), [filteredDebts, language, movementDate, movementSort, t]);
  const pageDebts = useMemo(() => sortedDebts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sortedDebts, page, rowsPerPage]);

  useEffect(() => setPage(0), [ledgerFilter, selectedMonth, selectedYear]);
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= sortedDebts.length) setPage(Math.max(0, Math.ceil(sortedDebts.length / rowsPerPage) - 1));
  }, [page, rowsPerPage, sortedDebts.length]);

  const handleDebtDialogClose = () => {
    setDebtDialogOpen(false);
    setSelectedDebt(undefined);
  };
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDebtToDelete(null);
  };
  const handleDebtSuccess = async () => {
    setPlanRefreshKey(value => value + 1);
    void queryClient.invalidateQueries({ queryKey: ['debts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['payment-plans'] });
    void loadDebts();
    if (!customerId) return;
    try {
      const totalDebtData = await apiClient.getCustomerTotalDebt(customerId);
      setTotalDebt(totalDebtData.totalDebt);
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, t('debts.loadError')));
    }
  };

  const openShareDialog = () => {
    setShareMessageEdited(false);
    setShareError(null);
    setShareDialogOpen(true);
  };

  const closeShareDialog = () => {
    if (pdfBusy) return;
    setShareDialogOpen(false);
    setShareError(null);
  };

  const createSummaryPdfBlob = async (): Promise<Blob | null> => {
    setPdfBusy(true);
    setShareError(null);
    try {
      return await createCustomerDebtPdf(createCustomerDebtSummaryDocument(summaryInput));
    } catch (pdfError: unknown) {
      setShareError(errorMessage(pdfError, t('customerDetail.shareFailed')));
      return null;
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = debtSummary.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  };

  const handleDownloadPdf = async () => {
    const blob = await createSummaryPdfBlob();
    if (blob) downloadBlob(blob);
  };

  const handleSharePdf = async () => {
    const blob = await createSummaryPdfBlob();
    if (!blob) return;

    const file = new File([blob], debtSummary.fileName, { type: 'application/pdf' });
    const canShareFiles = typeof navigator.canShare !== 'function'
      || navigator.canShare({ files: [file] });
    if (typeof navigator.share !== 'function' || !canShareFiles) {
      downloadBlob(blob);
      setShareNotice(t('customerDetail.pdfShareFallback'));
      return;
    }

    try {
      await navigator.share({
        title: t('customerDetail.shareDebtSummaryTitle'),
        text: debtSummary.balanceLine,
        files: [file],
      });
    } catch (shareError: unknown) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') return;
      setShareError(errorMessage(shareError, t('customerDetail.shareFailed')));
    }
  };

  const handleWhatsAppShare = () => {
    const message = shareMessage.trim() || debtSummary.message;
    if (!openWhatsApp({ phoneNumber: customer?.phone, message })) setShareError(t('customerDetail.shareFailed'));
  };

  const renderMovementActions = (debt: AccountMovement) => {
    if (!canManageDebts) return null;
    if (isSaleLinked(debt)) {
      return <Tooltip title={t('debts.saleMovementReadOnly')}><IconButton size="small" aria-label={t('debts.saleMovementReadOnly')} onClick={() => setSaleMovementNoticeOpen(true)}><LockIcon fontSize="small" /></IconButton></Tooltip>;
    }
    return <RowActionsButton
      aria-label={`${t('common.actions')}: ${accountMovementDescription(debt, t)}`}
      aria-haspopup="menu"
      onClick={event => { setMenuAnchor(event.currentTarget); setMenuDebt(toEditableDebt(debt)); }}
    />;
  };

  const renderAmount = (debt: AccountMovement) => {
    return <Typography fontWeight={700} sx={{ color: customerAccountAmountColor(debt.amount) }}>
      {formatCustomerAccountAmount(debt.amount)}
    </Typography>;
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}><CircularProgress /></Box>;
  if (error) return <Paper sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Paper>;
  if (!customer) return <Paper sx={{ p: 3 }}><Alert severity="warning">{t('customers.notFound')}</Alert></Paper>;

  const balanceCopy = totalDebt > 0
    ? t('customerDetail.customerOwes')
    : totalDebt < 0 ? t('customerDetail.youOwe') : t('customerDetail.noBalance');
  const balanceIsOwedToCustomer = totalDebt < 0;
  const balanceIsCustomerOwed = totalDebt > 0;
  const balanceColor = customerAccountAmountColor(totalDebt);
  const hasCustomerInfo = Boolean(customer.phone || customer.email || customer.company?.name || customer.taxIdentificationNumber || customer.createdAt || customer.address);
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const customerInitials = [customer.firstName, customer.lastName]
    .filter(Boolean)
    .map(part => Array.from(part.trim()).find(character => character.toLocaleLowerCase(locale) !== character.toLocaleUpperCase(locale)) || '')
    .join('')
    .toLocaleUpperCase(locale);
  const customerAvatar = (
    <Box aria-hidden="true" sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: 'rgba(35, 96, 68, .10)', fontSize: 13, fontWeight: 750, letterSpacing: '.02em' }}>
      {customerInitials || <PersonIcon fontSize="small" />}
    </Box>
  );
  const customerInfoSummary = [customer.phone, customer.email].filter(Boolean).join(' · ')
    || customer.company?.name
    || customer.address
    || (hasCustomerInfo ? t('customerDetail.infoSubtitle') : t('customerDetail.infoEmpty'));
  const customerInfoHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
      {customerAvatar}
      <Box sx={{ minWidth: 0 }}>
        <Typography component="span" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.35 }}>{t('customers.customerInfo')}</Typography>
        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
          {customerInfoSummary}
        </Typography>
      </Box>
    </Box>
  );
  const activeFilterCount = (ledgerFilter !== 'ALL' ? 1 : 0)
    + (selectedYear !== '' ? 1 : 0)
    + (selectedMonth !== '' ? 1 : 0);
  const monthFilterLabel = selectedMonth === '' ? '' : new Date(2020, selectedMonth - 1, 1)
    .toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'long' });
  const clearMovementFilters = () => {
    setLedgerFilter('ALL');
    setSelectedYear('');
    setSelectedMonth('');
  };
  const movementFilterButton = (
    <Badge
      badgeContent={activeFilterCount}
      color="warning"
      invisible={activeFilterCount === 0}
      overlap={isMobile ? 'circular' : 'rectangular'}
      sx={{ flexShrink: 0, '& .MuiBadge-badge': { fontWeight: 700, border: '2px solid', borderColor: 'background.paper' } }}
    >
      {isMobile ? (
        <IconButton aria-label={`${t('customerDetail.filterDates')}${activeFilterCount ? ` (${activeFilterCount})` : ''}`} aria-expanded={showFilters} aria-controls="customer-movement-filters" onClick={() => setShowFilters(value => !value)} sx={{ width: 44, height: 44, color: 'primary.main' }}>
          <FilterListIcon />
        </IconButton>
      ) : (
        <Button variant="outlined" aria-label={`${t('customerDetail.filterDates')}${activeFilterCount ? ` (${activeFilterCount})` : ''}`} aria-expanded={showFilters} aria-controls="customer-movement-filters" startIcon={<FilterListIcon />} endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowFilters(value => !value)} sx={{ bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
          {t('customerDetail.filterDates')}
        </Button>
      )}
    </Badge>
  );
  const movementFilterPanel = (
    <Collapse in={showFilters}>
      <Box id="customer-movement-filters" sx={{ mt: 1.5, p: { xs: 1.25, sm: 1.5 }, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <ToggleButtonGroup size="small" fullWidth={isMobile} exclusive value={ledgerFilter} onChange={(_event, value: LedgerFilter | null) => { if (value) setLedgerFilter(value); }} aria-label={t('debts.filter')} sx={{ alignSelf: { xs: 'stretch', lg: 'auto' }, flexShrink: 0, '& .MuiToggleButton-root': { flex: { xs: 1, lg: 'initial' }, textTransform: 'none', px: { xs: .5, sm: 1.5 }, minHeight: 40 } }}>
            <ToggleButton value="ALL">{t('debts.allRecords')}</ToggleButton>
            <ToggleButton value="DEBT">{t('debts.debtEntries')}</ToggleButton>
            <ToggleButton value="PAYMENT">{t('debts.paymentEntries')}</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, lg: 140 }, flex: 1 }}>
              <InputLabel>{t('debts.year')}</InputLabel>
              <Select value={selectedYear} label={t('debts.year')} onChange={event => setSelectedYear(event.target.value ? Number(event.target.value) : '')}>
                <MenuItem value="">{t('debts.allYears')}</MenuItem>
                {yearOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: 0, lg: 160 }, flex: 1 }}>
              <InputLabel>{t('debts.month')}</InputLabel>
              <Select value={selectedMonth} label={t('debts.month')} onChange={event => setSelectedMonth(event.target.value ? Number(event.target.value) : '')}>
                <MenuItem value="">{t('debts.allMonths')}</MenuItem>
                {monthOptions.map(month => <MenuItem key={month} value={month}>{new Date(2020, month - 1, 1).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'long' })}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>
    </Collapse>
  );
  const activeMovementFilterChips = activeFilterCount > 0 && (
    <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{t('debts.activeFilters')}</Typography>
      {ledgerFilter !== 'ALL' && <Chip size="small" label={`${t('debts.movementType')}: ${ledgerFilter === 'DEBT' ? t('debts.debtEntries') : t('debts.paymentEntries')}`} onDelete={() => setLedgerFilter('ALL')} sx={{ bgcolor: 'action.selected' }} />}
      {selectedYear !== '' && <Chip size="small" label={`${t('debts.year')}: ${selectedYear}`} onDelete={() => setSelectedYear('')} sx={{ bgcolor: 'action.selected' }} />}
      {selectedMonth !== '' && <Chip size="small" label={`${t('debts.month')}: ${monthFilterLabel}`} onDelete={() => setSelectedMonth('')} sx={{ bgcolor: 'action.selected' }} />}
      <Button size="small" onClick={clearMovementFilters} sx={{ minWidth: 0 }}>{t('common.clearFilters')}</Button>
    </Stack>
  );
  return (
    <Box sx={{ pb: 4 }}>
      {isMobile && <Box sx={{ pb: 'calc(58px + env(safe-area-inset-bottom))' }}>
        <Box sx={{ px: 2, pt: 2, pb: 2.25 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '.08em' }}>
            {t('customerDetail.currentBalance')}
          </Typography>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1} sx={{ mt: .25 }}>
            <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1.2, color: balanceColor }}>
              {formatCustomerAccountAmount(totalDebt)}
            </Typography>
            {totalDebt !== 0 && <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', flexShrink: 0 }}>{balanceCopy}</Typography>}
          </Stack>
          <Stack spacing={1} sx={{ mt: 2 }}>
            {canManageDebts && <Stack direction="row" spacing={1}>
              <Button fullWidth variant="outlined" color="success" startIcon={<ArrowDownwardIcon />} onClick={handleAddPayment} sx={{ minHeight: 44, textTransform: 'none', whiteSpace: 'nowrap' }}>{t('debts.addPayment')}</Button>
              <Button fullWidth variant="outlined" color="error" startIcon={<ArrowUpwardIcon />} onClick={handleAddDebt} sx={{ minHeight: 44, textTransform: 'none', whiteSpace: 'nowrap' }}>{t('debts.addDebt')}</Button>
            </Stack>}
            <Button fullWidth variant="outlined" startIcon={<ShareIcon />} onClick={openShareDialog} sx={{ minHeight: 44, textTransform: 'none' }}>
              {t('customerDetail.shareDebtSummary')}
            </Button>
          </Stack>
        </Box>

        <Paper variant="outlined" sx={{ mx: 2, borderRadius: 2, overflow: 'hidden', borderColor: 'divider' }}>
          {hasCustomerInfo ? (
            <Button fullWidth color="inherit" aria-expanded={showCustomerInfo} onClick={() => setShowCustomerInfo(value => !value)} sx={{ minHeight: 68, px: 1.5, py: 1.25, justifyContent: 'space-between', gap: 1, textAlign: 'left', textTransform: 'none', '&:hover': { bgcolor: 'action.hover' } }}>
              {customerInfoHeader}
              {showCustomerInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button>
          ) : (
            <Box sx={{ minHeight: 68, px: 1.5, py: 1.25, display: 'flex', alignItems: 'center' }}>{customerInfoHeader}</Box>
          )}
          {hasCustomerInfo && <Collapse in={showCustomerInfo}>
            <Box sx={{ display: 'grid', gap: 1.5, px: 2, pt: 1.5, pb: 2, borderTop: 1, borderColor: 'divider' }}>
              {customer.phone && <InfoItem icon={<PhoneIcon />} label={t('customers.phone')} value={customer.phone} />}
              {customer.email && <InfoItem icon={<EmailIcon />} label={t('customers.email')} value={customer.email} />}
              {customer.company?.name && <InfoItem icon={<BusinessIcon />} label={t('customers.company')} value={customer.company.name} />}
              {customer.taxIdentificationNumber && <InfoItem icon={<BadgeIcon />} label={t('customers.taxIdentificationNumber')} value={customer.taxIdentificationNumber} />}
              {customer.createdAt && <InfoItem icon={<CalendarTodayIcon />} label={t('common.createdAt')} value={formatDate(customer.createdAt, language)} />}
              {customer.address && <InfoItem icon={<LocationOnIcon />} label={t('customers.address')} value={customer.address} />}
            </Box>
          </Collapse>}
        </Paper>

        <Box sx={{ mx: 2 }}><CustomerPaymentPlans customerId={customerId!} customerName={customerName} customerPhone={customer.phone} canManage={canManageDebts} onChanged={handleDebtSuccess} refreshKey={planRefreshKey} /></Box>
        <ListSurface sx={{ mx: 2 }}>
        <ListSurface.Toolbar sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700}>{t('debts.ledgerTitle')}</Typography>
            {movementFilterButton}
          </Stack>
          {movementFilterPanel}
          {activeMovementFilterChips}
        </ListSurface.Toolbar>

        <ListSurface.Content>
        <Box sx={{ px: 2 }}>
          {pageDebts.map(debt => {
            return <Box key={debt.id} sx={{ display: 'grid', gridTemplateColumns: '38px minmax(0, 1fr) auto', columnGap: 1.25, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <DebtMovementIcon debt={debt} />
              <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={650} sx={{ overflowWrap: 'anywhere', lineHeight: 1.35 }}>{accountMovementDescription(debt, t)}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{formatDateTime(debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' })}</Typography>
                {debt.createdByUsername && <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{debt.createdByUsername}</Typography>}
              </Box>
              <Stack alignItems="flex-end" justifyContent="space-between" sx={{ minWidth: 88, ml: .5 }}>
                <Typography fontWeight={750} sx={{ color: customerAccountAmountColor(debt.amount), whiteSpace: 'nowrap' }}>{formatCustomerAccountAmount(debt.amount)}</Typography>
                {renderMovementActions(debt)}
              </Stack>
            </Box>;
          })}
          {pageDebts.length === 0 && <EmptyMovements message={t('debts.noDebts')} />}
        </Box>
        </ListSurface.Content>
        <ListSurface.Footer>
          <PaginationFooter pageIndex={page} pageSize={rowsPerPage} totalPages={Math.ceil(sortedDebts.length / rowsPerPage)} totalElements={sortedDebts.length} showingLabel={t('common.showing')} ofLabel={t('common.of')} pageSizeLabel={t('common.rowsPerPage')} mobileCompact previousPageLabel={t('customers.previousPage')} nextPageLabel={t('customers.nextPage')} onPageChange={setPage} onPageSizeChange={pageSize => { setRowsPerPage(pageSize); setPage(0); }} />
        </ListSurface.Footer>
        </ListSurface>
      </Box>}

      {!isMobile && <>
      {!isMobile && <Button startIcon={<BackIcon />} onClick={handleBack} color="inherit" sx={{ mb: 1, ml: -1 }}>{t('layout.back')}</Button>}
      <PageHeader
        title={customerName}
        icon={<PersonIcon />}
        accent="customers"
        description={customer.phone || customer.email ? (
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {customer.phone && <Typography variant="body2" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}><PhoneIcon fontSize="small" />{customer.phone}</Typography>}
            {customer.email && <Typography variant="body2" color="text.secondary">{customer.email}</Typography>}
          </Stack>
        ) : undefined}
        actions={<Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="outlined" startIcon={<ShareIcon />} onClick={openShareDialog}>{t('customerDetail.shareDebtSummary')}</Button>
          {canManageDebts && <>
          <PageHeaderActionButton color="success" startIcon={<ArrowDownwardIcon />} onClick={handleAddPayment}>{t('debts.addPayment')}</PageHeaderActionButton>
          <PageHeaderActionButton color="error" startIcon={<ArrowUpwardIcon />} onClick={handleAddDebt}>{t('debts.addDebt')}</PageHeaderActionButton>
          </>}
        </Stack>}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.05fr) minmax(0, .95fr)' }, gap: 2.5, mb: 2.5 }}>
        <Card sx={{ overflow: 'hidden', background: theme => {
          if (!balanceIsCustomerOwed && !balanceIsOwedToCustomer) return theme.palette.background.paper;
          if (theme.palette.mode === 'dark') {
            return balanceIsCustomerOwed
              ? 'linear-gradient(135deg, rgba(104, 52, 48, .55), rgba(37, 30, 29, .98) 72%)'
              : 'linear-gradient(135deg, rgba(38, 86, 58, .55), rgba(25, 36, 30, .98) 72%)';
          }
          return balanceIsCustomerOwed
            ? 'linear-gradient(125deg, #f9e9e5 0%, #fff8f3 58%, #f8e5df 100%)'
            : 'linear-gradient(125deg, #eaf4e8 0%, #fffdf8 58%, #e5f0e4 100%)';
        },
          borderColor: balanceIsCustomerOwed ? 'rgba(168, 76, 64, .42)' : balanceIsOwedToCustomer ? 'rgba(63, 112, 71, .38)' : 'divider' }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 }, '&:last-child': { pb: { xs: 2.5, sm: 3 } } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing=".09em">{t('customerDetail.currentBalance')}</Typography>
                <Typography variant="h3" sx={{ mt: .5, fontSize: { xs: '2rem', sm: '2.5rem' }, fontWeight: 800, letterSpacing: '-.035em', color: balanceColor }}>
                  {formatCustomerAccountAmount(totalDebt)}
                </Typography>
                {totalDebt !== 0 && <Typography variant="body1" fontWeight={650} sx={{ mt: .5 }}>{balanceCopy}</Typography>}
              </Box>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 52, height: 52, borderRadius: 2.5, color: balanceIsCustomerOwed ? 'error.dark' : balanceIsOwedToCustomer ? 'success.dark' : 'text.secondary', bgcolor: balanceIsCustomerOwed ? 'rgba(168, 76, 64, .13)' : balanceIsOwedToCustomer ? 'rgba(63, 112, 71, .14)' : 'action.hover', border: 1, borderColor: balanceIsCustomerOwed ? 'rgba(168, 76, 64, .24)' : balanceIsOwedToCustomer ? 'rgba(63, 112, 71, .24)' : 'divider' }}>
                <WalletIcon />
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{t('customerDetail.balanceCaption')}</Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.25 }}>
              {customerAvatar}
              <Box><Typography variant="h6" fontWeight={750}>{t('customers.customerInfo')}</Typography></Box>
            </Box>
            {hasCustomerInfo ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
              {customer.phone && <InfoItem icon={<PhoneIcon />} label={t('customers.phone')} value={customer.phone} />}
              {customer.email && <InfoItem icon={<EmailIcon />} label={t('customers.email')} value={customer.email} />}
              {customer.company?.name && <InfoItem icon={<BusinessIcon />} label={t('customers.company')} value={customer.company.name} />}
              {customer.taxIdentificationNumber && <InfoItem icon={<BadgeIcon />} label={t('customers.taxIdentificationNumber')} value={customer.taxIdentificationNumber} />}
              {customer.createdAt && <InfoItem icon={<CalendarTodayIcon />} label={t('common.createdAt')} value={formatDate(customer.createdAt, language)} />}
              {customer.address && <InfoItem icon={<LocationOnIcon />} label={t('customers.address')} value={customer.address} wide />}
            </Box> : <Typography variant="body2" color="text.secondary">{t('customerDetail.infoEmpty')}</Typography>}
          </CardContent>
        </Card>
      </Box>

      <CustomerPaymentPlans customerId={customerId!} customerName={customerName} customerPhone={customer.phone} canManage={canManageDebts} onChanged={handleDebtSuccess} refreshKey={planRefreshKey} />
      <ListSurface>
        <ListSurface.Toolbar sx={{ px: { xs: 0, md: 0 }, pt: { xs: 0, md: 0 }, pb: { xs: 0, md: 0 } }}>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700}>{t('debts.ledgerTitle')}</Typography>
            {movementFilterButton}
          </Stack>
          {movementFilterPanel}
          {activeMovementFilterChips}
        </Box>
        </ListSurface.Toolbar>

        <ListSurface.Content>
        {isMobile ? <Stack spacing={1} sx={{ p: 1.5 }}>
          {pageDebts.map(debt => <Card variant="outlined" key={debt.id} sx={{ borderRadius: 2.5 }}>
            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box sx={{ minWidth: 0 }}><Typography variant="body2" color="text.secondary">{formatDateTime(debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' })}</Typography><Typography sx={{ mt: .5, overflowWrap: 'anywhere' }}>{accountMovementDescription(debt, t)}</Typography></Box>
                {renderMovementActions(debt)}
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{debt.amount < 0 ? t('debts.payment') : t('debts.debtEntries')}{debt.createdByUsername ? ` · ${debt.createdByUsername}` : ''}</Typography>{renderAmount(debt)}
              </Stack>
            </CardContent>
          </Card>)}
          {pageDebts.length === 0 && <EmptyMovements message={t('debts.noDebts')} />}
        </Stack> : <TableContainer>
          <Table>
            <TableHead><TableRow>
              {([
                ['lastActivityDateTime', t('debts.transactionDateTime')],
                ['description', t('debts.description')],
                ['createdByUsername', t('debts.createdBy')],
                ['amount', t('debts.amount')],
              ] as [SortField, string][]).map(([field, label]) => <DataTableHeaderCell key={field} align={field === 'amount' ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }} sort={{
                active: movementSort.sort === field,
                direction: movementSort.sort === field && movementSort.direction === 'desc' ? 'desc' : 'asc',
                onClick: () => { setMovementSort(current => nextTableSort(current, field)); setPage(0); },
              }}>{label}</DataTableHeaderCell>)}
              {canManageDebts && <DataTableHeaderCell align="right">{t('common.actions')}</DataTableHeaderCell>}
            </TableRow></TableHead>
            <TableBody>
              {pageDebts.map(debt => <TableRow hover key={debt.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(debt.lastActivityDateTime || debt.transactionDateTime || debt.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' })}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>{accountMovementDescription(debt, t)}</TableCell>
                <TableCell>{debt.createdByUsername || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{renderAmount(debt)}</TableCell>
                {canManageDebts && <TableCell align="right" sx={{ width: 64 }}>{renderMovementActions(debt)}</TableCell>}
              </TableRow>)}
              {pageDebts.length === 0 && <TableRow><TableCell colSpan={canManageDebts ? 5 : 4}><EmptyMovements message={t('debts.noDebts')} /></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>}
        </ListSurface.Content>
        <ListSurface.Footer>
        {sortedDebts.length > 0 && (
          <PaginationFooter
            pageIndex={page}
            pageSize={rowsPerPage}
            totalPages={Math.ceil(sortedDebts.length / rowsPerPage)}
            totalElements={sortedDebts.length}
            showingLabel={t('common.showing')}
            ofLabel={t('common.of')}
            pageSizeLabel={t('common.rowsPerPage')}
            onPageChange={setPage}
            onPageSizeChange={pageSize => { setRowsPerPage(pageSize); setPage(0); }}
          />
        )}
        </ListSurface.Footer>
      </ListSurface>
      </>}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMovementMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { if (menuDebt) handleEditDebt(menuDebt); closeMovementMenu(); }}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>{t('common.edit')}</ListItemText></MenuItem>
        <MenuItem onClick={() => { if (menuDebt) handleDeleteDebt(menuDebt); closeMovementMenu(); }} sx={{ color: 'error.main' }}><ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon><ListItemText>{t('common.delete')}</ListItemText></MenuItem>
      </Menu>
      <Snackbar open={saleMovementNoticeOpen} autoHideDuration={4500} onClose={() => setSaleMovementNoticeOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 24 } }}>
        <Alert severity="info" onClose={() => setSaleMovementNoticeOpen(false)}>{t('debts.saleMovementReadOnly')}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(shareNotice)} autoHideDuration={4500} onClose={() => setShareNotice(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setShareNotice(null)}>{shareNotice}</Alert>
      </Snackbar>
      <AppDialog
        open={shareDialogOpen}
        onClose={closeShareDialog}
        presentation="workflow"
        fullWidth
        maxWidth="sm"
        closeDisabled={pdfBusy}
      >
        <AppDialogHeader icon={<ShareIcon />} subtitle={customerName}>
          {t('customerDetail.shareDebtSummaryTitle')}
        </AppDialogHeader>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('customerDetail.shareDebtSummaryHint')}
          </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="share-period-label">{t('customerDetail.sharePeriod')}</InputLabel>
              <Select
                labelId="share-period-label"
                label={t('customerDetail.sharePeriod')}
                value={sharePeriod}
                onChange={event => { setSharePeriod(event.target.value as SharePeriod); setShareMessageEdited(false); setShareError(null); }}
              >
                <MenuItem value="LAST_MONTH">{t('customerDetail.lastMonth')}</MenuItem>
                <MenuItem value="LAST_TWO_MONTHS">{t('customerDetail.lastTwoMonths')}</MenuItem>
                <MenuItem value="LAST_YEAR">{t('customerDetail.lastYear')}</MenuItem>
                <MenuItem value="ALL">{t('customerDetail.allMovements')}</MenuItem>
                <MenuItem value="CUSTOM">{t('customerDetail.customRange')}</MenuItem>
              </Select>
            </FormControl>
          {sharePeriod === 'CUSTOM' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField fullWidth type="date" label={t('filters.startDate')} InputLabelProps={{ shrink: true }} value={shareStartDate} onChange={event => { setShareStartDate(event.target.value); setShareMessageEdited(false); }} />
            <TextField fullWidth type="date" label={t('filters.endDate')} InputLabelProps={{ shrink: true }} value={shareEndDate} onChange={event => { setShareEndDate(event.target.value); setShareMessageEdited(false); }} />
          </Stack>}
          <TextField
            fullWidth
            multiline
            rows={10}
            label={t('customerDetail.shareMessageLabel')}
            value={shareMessage}
            onChange={event => { setShareMessage(event.target.value); setShareMessageEdited(true); }}
            inputProps={{ 'aria-label': t('customerDetail.shareMessageLabel') }}
          />
          {shareError && <Alert severity="error" sx={{ mt: 2 }}>{shareError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareDialog} disabled={pdfBusy}>{t('common.cancel')}</Button>
          <Button onClick={handleDownloadPdf} disabled={pdfBusy} startIcon={pdfBusy ? <CircularProgress size={18} /> : <DownloadIcon />}>
            {pdfBusy ? t('customerDetail.pdfPreparing') : t('customerDetail.downloadPdf')}
          </Button>
          <Button onClick={handleSharePdf} disabled={pdfBusy} startIcon={<PictureAsPdfIcon />}>
            {t('customerDetail.sharePdf')}
          </Button>
          <Button variant="contained" color="success" onClick={handleWhatsAppShare} disabled={pdfBusy} startIcon={<WhatsAppIcon />}>
            {t('customerDetail.whatsappShare')}
          </Button>
        </DialogActions>
      </AppDialog>
      <DebtDialog open={debtDialogOpen} onClose={handleDebtDialogClose} debt={selectedDebt} onSuccess={handleDebtSuccess} prefilledCustomer={customer} />
      <PaymentDialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} onSuccess={handleDebtSuccess} prefilledCustomer={customer} />
      <DeleteDebtDialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} debt={debtToDelete} onSuccess={handleDebtSuccess} />
    </Box>
  );
};

const InfoItem: React.FC<{ icon: React.ReactNode; label: string; value: string; wide?: boolean }> = ({ icon, label, value, wide }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
    <Box sx={{ display: 'grid', placeItems: 'center', color: 'text.secondary', mt: .1 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography></Box>
  </Box>
);

const EmptyMovements: React.FC<{ message: string }> = ({ message }) => <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message}</Typography></Box>;

export default CustomerDetail;
